import * as React from 'react';
import { Box, Typography } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

export const REVIEW_CATEGORIES = [
  'Anime',
  'TV Shows',
  'Movies',
  'Manga',
  'Games',
  'Books',
];

const LEGACY_REVIEW_CATEGORIES = {
  'TV Show': 'TV Shows',
  Misc: 'Books',
};

export function normaliseReviewCategory(category) {
  return LEGACY_REVIEW_CATEGORIES[category] || category;
}

export function normaliseReviewCategories(entry) {
  const rawCategories = Array.isArray(entry?.categories) && entry.categories.length > 0
    ? entry.categories
    : [entry?.category];
  const categories = rawCategories
    .map(normaliseReviewCategory)
    .filter((category, index, all) => REVIEW_CATEGORIES.includes(category) && all.indexOf(category) === index);
  return categories.length > 0 ? categories : ['Anime'];
}

export function normaliseFavoriteRanks(entry, categories = normaliseReviewCategories(entry)) {
  const ranks = {};
  if (entry?.favoriteRanks && typeof entry.favoriteRanks === 'object' && !Array.isArray(entry.favoriteRanks)) {
    Object.entries(entry.favoriteRanks).forEach(([rawCategory, rawRank]) => {
      const category = normaliseReviewCategory(rawCategory);
      const rank = Number(rawRank);
      if (categories.includes(category) && rank >= 1 && rank <= 5) ranks[category] = rank;
    });
  }
  const legacyRank = Number(entry?.favoriteRank);
  if (Object.keys(ranks).length === 0 && legacyRank >= 1 && legacyRank <= 5) {
    ranks[categories[0]] = legacyRank;
  }
  return ranks;
}

export const EMPTY_REVIEW_FORM = {
  id: null,
  category: 'Anime',
  categories: ['Anime'],
  title: '',
  score: 3,
  imageUrl: '',
  favoriteRank: '',
  favoriteRanks: {},
  completedDate: '',
  alternateTitles: '',
  review: '',
  videoUrl: '',
  videoLinks: [{ url: '', label: '' }],
  source: '',
  sourceId: '',
  sourceUrl: '',
};

const SCORE_COLORS = {
  1: '#fef3c7',
  2: '#fde68a',
  3: '#fcd34d',
  4: '#fbbf24',
  5: '#f59e0b',
};

const SCORE_GLOW_ALPHA = {
  1: '2b',
  2: '4d',
  3: '73',
  4: '9e',
  5: 'c7',
};

export function ScoreStar({ score, compact = false }) {
  const safeScore = Math.max(1, Math.min(5, Number(score) || 1));
  const size = compact ? 52 : 62;

  return (
    <Box
      role="img"
      aria-label={`${safeScore} out of 5 stars`}
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        filter: [
          `drop-shadow(0 3px ${2 + safeScore}px ${SCORE_COLORS[safeScore]}${SCORE_GLOW_ALPHA[safeScore]})`,
          `drop-shadow(0 0 ${safeScore * 1.25}px ${SCORE_COLORS[safeScore]}${SCORE_GLOW_ALPHA[safeScore]})`,
        ].join(' '),
      }}
    >
      <StarRoundedIcon
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          fontSize: size,
          color: SCORE_COLORS[safeScore],
          stroke: '#8a5a00',
          strokeWidth: 0.45,
        }}
      />
      <Typography
        component="span"
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#3b2a00',
          fontWeight: 950,
          fontSize: compact ? 13 : 16,
          lineHeight: 1,
          transform: 'translateY(1.5px)',
        }}
      >
        {safeScore}
      </Typography>
    </Box>
  );
}

export function ReviewPoster({ entry, compact = false }) {
  const [failed, setFailed] = React.useState(false);
  const width = compact ? 64 : { xs: 60, sm: 78 };
  const height = compact ? 64 : { xs: 60, sm: 78 };

  React.useEffect(() => {
    setFailed(false);
  }, [entry.imageUrl]);

  if (!entry.imageUrl || failed) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(145deg, #27272a, #09090b)',
          color: '#a1a1aa',
          fontSize: 11,
          fontWeight: 750,
          textAlign: 'center',
          px: 0.5,
        }}
      >
        No image
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={entry.imageUrl}
      alt={`Cover for ${entry.title}`}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width,
        height,
        objectFit: 'cover',
        borderRadius: 2,
        flexShrink: 0,
        backgroundColor: '#27272a',
      }}
    />
  );
}
